import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { Landmark, Pencil, Plus } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { CopyButton } from '@/components/coins';
import { PageHeader } from '@/components/layout/AppShell';
import { Page } from '@/components/motion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, Input, Label } from '@/components/ui/input';
import { EmptyState, ErrorState, Skeleton, Switch } from '@/components/ui/misc';
import { adminApi } from '@/lib/api/endpoints';
import { isApiError, userMessage } from '@/lib/api/errors';
import type { BankAccount, BankAccountIn } from '@/lib/api/types';
import { cn } from '@/lib/utils';

const EMPTY: BankAccountIn = { bank_name: '', account_name: '', account_number: '', is_active: true };

function AccountDialog({ account, open, onOpenChange }: { account: BankAccount | null; open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const [f, setF] = React.useState<BankAccountIn>(EMPTY);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  React.useEffect(() => {
    if (open) {
      setF(account ? { bank_name: account.bank_name, account_name: account.account_name, account_number: account.account_number, is_active: account.is_active } : EMPTY);
      setErrors({});
    }
  }, [open, account]);

  const save = useMutation({
    mutationFn: () => (account ? adminApi.updateBankAccount(account.id, f) : adminApi.createBankAccount(f)),
    onSuccess: () => {
      toast.success(account ? 'Account updated' : 'Account added');
      qc.invalidateQueries({ queryKey: ['admin', 'bank-accounts'] });
      qc.invalidateQueries({ queryKey: ['pricing'] });
      onOpenChange(false);
    },
    onError: (e) => {
      if (isApiError(e, 422)) setErrors(e.fieldErrors);
      toast.error(userMessage(e));
    },
  });
  const valid = f.bank_name.trim() && f.account_name.trim() && f.account_number.trim();
  const set = (k: 'bank_name' | 'account_name' | 'account_number') => (e: React.ChangeEvent<HTMLInputElement>) => setF((x) => ({ ...x, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{account ? 'Edit bank account' : 'Add bank account'}</DialogTitle>
          <DialogDescription>Customers transfer money to active accounts when they buy coins.</DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (valid) save.mutate(); }}>
          <Field label="Bank name" htmlFor="bank_name" error={errors.bank_name}>
            <Input id="bank_name" value={f.bank_name} onChange={set('bank_name')} placeholder="Commercial Bank of Ethiopia" />
          </Field>
          <Field label="Account holder name" htmlFor="account_name" error={errors.account_name}>
            <Input id="account_name" value={f.account_name} onChange={set('account_name')} />
          </Field>
          <Field label="Account number" htmlFor="account_number" error={errors.account_number}>
            <Input id="account_number" className="font-mono" value={f.account_number} onChange={set('account_number')} />
          </Field>
          <div className="flex items-center justify-between">
            <Label htmlFor="acc-active">Shown to customers</Label>
            <Switch id="acc-active" checked={f.is_active} onCheckedChange={(v) => setF((x) => ({ ...x, is_active: v }))} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" loading={save.isPending} disabled={!valid}>{account ? 'Save' : 'Add account'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AdminBankAccountsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'bank-accounts'], queryFn: adminApi.bankAccounts });
  const [editing, setEditing] = React.useState<BankAccount | null>(null);
  const [open, setOpen] = React.useState(false);
  const toggle = useMutation({
    mutationFn: (a: BankAccount) => adminApi.updateBankAccount(a.id, { is_active: !a.is_active }),
    onSuccess: (a) => {
      toast.success(a.is_active ? 'Account shown to customers' : 'Account hidden from customers');
      qc.invalidateQueries({ queryKey: ['admin', 'bank-accounts'] });
      qc.invalidateQueries({ queryKey: ['pricing'] });
    },
    onError: (e) => toast.error(userMessage(e)),
  });

  return (
    <Page>
      <PageHeader
        eyebrow="Admin"
        title="Bank accounts"
        description="Accounts are never deleted, because old purchases point to them. Hide one instead."
        actions={
          <Button variant="brand" onClick={() => { setEditing(null); setOpen(true); }}>
            <Plus /> Add account
          </Button>
        }
      />
      {q.isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} className="h-44 rounded-2xl" />)}
        </div>
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : q.data.length === 0 ? (
        <Card>
          <EmptyState icon={<Landmark />} title="No bank accounts" description="Add one so customers can buy coins." />
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence>
            {q.data.map((a, i) => (
              <motion.div key={a.id} layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <div
                    className={cn(
                      'group relative overflow-hidden rounded-2xl p-5 text-white shadow-xl transition-all hover:-translate-y-1',
                      a.is_active ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-950' : 'bg-gradient-to-br from-slate-500 to-slate-700 opacity-75 grayscale',
                    )}
                  >
                    <div className="absolute -right-10 -top-10 size-32 rounded-full bg-white/10 blur-2xl transition-transform group-hover:scale-150" />
                    <div className="relative flex items-start justify-between gap-3">
                      <span className="flex size-10 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/20">
                        <Landmark className="size-5" />
                      </span>
                      <Badge tone={a.is_active ? 'success' : 'neutral'} className="bg-white/10 text-white ring-white/20">
                        {a.is_active ? 'Active' : 'Hidden'}
                      </Badge>
                    </div>
                    <p className="relative mt-4 truncate text-lg font-bold">{a.bank_name}</p>
                    <p className="relative truncate text-sm text-white/70">{a.account_name}</p>
                    <div className="relative mt-4 flex items-center justify-between rounded-lg bg-white/10 py-1 pl-3 pr-1 [&_button]:text-white/70 [&_button:hover]:bg-white/10 [&_button:hover]:text-white">
                      <span className="font-mono tracking-wider">{a.account_number}</span>
                      <CopyButton value={a.account_number} label="Account number" />
                    </div>
                    <div className="relative mt-4 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-white/70">
                        <Switch checked={a.is_active} onCheckedChange={() => toggle.mutate(a)} disabled={toggle.isPending} label="Shown to customers" />
                        Shown to customers
                      </div>
                      <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={() => { setEditing(a); setOpen(true); }}>
                        <Pencil /> Edit
                      </Button>
                    </div>
                  </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
      <AccountDialog account={editing} open={open} onOpenChange={setOpen} />
    </Page>
  );
}
